import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Pencil, Wand2 } from "lucide-react";
import { useQuery as useZeroQuery } from "@rocicorp/zero/react";
import { appQueries } from "@production-hub/shared";
import { useDefaultFunctions } from "@/hooks/useDefaultFunctions";
import { useAppSetting } from "@/hooks/useAppSettings";
import type { InitializeFunctionsConfig } from "@/pages/DefaultFunctionsPage";
import type {
  LocationType,
  ProjectFunctionGroup,
  ProjectFunctionSeries,
} from "@/hooks/useProjectPeople";
import { cn } from "@/lib/utils";

interface FunctionRowState {
  existingSeriesId: string | null;
  defaultFunctionId: string | null;
  projectFunctionGroupId: string | null;
  projectFunctionGroupName: string | null;
  projectFunctionGroupLocationType: LocationType;
  name: string;
  systemTag: string | null;
  locationType: LocationType;
  enabled: boolean;
  defaultCount: number;
  dayOverrides: Record<string, number | undefined>;
  selectedDays: Record<string, boolean>;
  daySelectionMode: "all" | "event" | "custom";
}

type CameraPlanQueryRow = {
  id: string;
  eventDate?: number | string | null;
  cameras?: {
    id: string;
    icon?: string | null;
    cameraPlanID: string;
    customValues?: { unmanned?: boolean } | null;
  }[];
};

interface InitializeProjectFunctionsDialogProps {
  projectId: string;
  planningDays: Date[];
  eventDays: Date[];
  existingFunctions: ProjectFunctionSeries[];
  projectFunctionGroups: ProjectFunctionGroup[];
  onInitialize: (
    functions: {
      name: string;
      project_function_group_id?: string | null;
      project_function_group_name?: string | null;
      project_function_group_location_type?: LocationType;
      required_count: number;
      selected_days: string[];
      system_tag: string | null;
      day_overrides: { date: string; count: number }[];
    }[],
  ) => void;
  onUpdateSeries?: (
    id: string,
    updates: {
      name?: string;
      project_function_group_id?: string | null;
      required_count?: number;
      selected_days?: string[];
      system_tag?: string | null;
      location_type?: LocationType;
    },
  ) => void;
  onUpdateDayOverride?: (
    dayId: string,
    requiredCountOverride: number | null,
  ) => void;
  isLoading?: boolean;
  triggerClassName?: string;
}

type CameraPlanWithCameras = {
  id: string;
  eventDate?: number | string | null;
  cameras?: {
    id: string;
    icon?: string | null;
    cameraPlanID: string;
    customValues?: { unmanned?: boolean } | null;
  }[];
};

function toDateStr(day: Date) {
  return format(day, "yyyy-MM-dd");
}

function toDateString(value: number | string | null | undefined) {
  if (typeof value === "number") {
    return new Date(value).toISOString().slice(0, 10);
  }
  return value ? String(value).slice(0, 10) : null;
}

function getDaySelectionMode(
  planningDays: Date[],
  eventDaySet: Set<string>,
  selectedDays: Record<string, boolean>,
): "all" | "event" | "custom" {
  const planningDateStrings = planningDays.map(toDateStr);
  const allSelected =
    planningDateStrings.length > 0 &&
    planningDateStrings.every((dateStr) => selectedDays[dateStr]);

  if (allSelected) {
    return "all";
  }

  const matchesEventDates =
    planningDateStrings.length > 0 &&
    planningDateStrings.every(
      (dateStr) => Boolean(selectedDays[dateStr]) === eventDaySet.has(dateStr),
    );

  return matchesEventDates ? "event" : "custom";
}

function getRowGroupKey(row: FunctionRowState) {
  if (row.projectFunctionGroupId) return row.projectFunctionGroupId;
  if (row.projectFunctionGroupName)
    return `template:${row.projectFunctionGroupName}`;
  return "__ungrouped__";
}

function getRowGroupLabel(row: FunctionRowState) {
  return row.projectFunctionGroupName ?? "Ungrouped";
}

function getGroupSelectValue(row: FunctionRowState) {
  if (row.projectFunctionGroupId) return row.projectFunctionGroupId;
  if (row.projectFunctionGroupName)
    return `template:${row.projectFunctionGroupName}`;
  return "none";
}

export function InitializeProjectFunctionsDialog({
  projectId,
  planningDays,
  eventDays,
  existingFunctions,
  projectFunctionGroups,
  onInitialize,
  onUpdateSeries,
  onUpdateDayOverride,
  isLoading,
  triggerClassName,
}: InitializeProjectFunctionsDialogProps) {
  const [open, setOpen] = useState(false);
  const { data: defaultFunctions, isLoading: loadingDefaults } =
    useDefaultFunctions();
  const { data: initConfig } = useAppSetting<InitializeFunctionsConfig>(
    "initialize_functions_config",
  );

  const mode = existingFunctions.length > 0 ? "bulk-update" : "initialize";

  const cameraQuery =
    open && projectId
      ? appQueries.cameraPlans.withCamerasByProject({ projectID: projectId })
      : undefined;
  const [cameraPlans] = useZeroQuery(cameraQuery, {
    enabled: open && !!projectId,
  });

  const cameraData = useMemo(() => {
    const plans = ((cameraPlans ?? []) as CameraPlanQueryRow[]).map((plan) => ({
      id: plan.id,
      event_date: toDateString(plan.eventDate),
    }));
    const cameras = ((cameraPlans ?? []) as CameraPlanQueryRow[]).flatMap(
      (plan) =>
        (plan.cameras ?? []).map((camera) => ({
          id: camera.id,
          icon: camera.icon,
          camera_plan_id: camera.cameraPlanID,
          custom_values: camera.customValues ?? null,
        })),
    );

    return { cameras, plans };
  }, [cameraPlans]);

  // Per-day camera counts from camera plans
  const cameraDayCounts = useMemo(() => {
    if (!cameraData?.cameras?.length || !cameraData?.plans?.length) return {};

    const result: Record<string, { camera: number; drone: number }> = {};

    for (const day of planningDays) {
      const dateStr = toDateStr(day);
      const matchingPlanIds = new Set(
        cameraData.plans
          .filter((p) => p.event_date === dateStr || !p.event_date)
          .map((p) => p.id),
      );
      const matchingCameras = cameraData.cameras.filter(
        (c) =>
          matchingPlanIds.has(c.camera_plan_id) &&
          c.custom_values?.unmanned !== true,
      );

      result[dateStr] = {
        camera: matchingCameras.filter((c) => c.icon !== "drone").length,
        drone: matchingCameras.filter((c) => c.icon === "drone").length,
      };
    }

    return result;
  }, [cameraData, planningDays]);

  const [rows, setRows] = useState<FunctionRowState[]>([]);

  // Track original state of existing rows for diffing on submit
  const originalExistingRows = useRef<Record<string, FunctionRowState>>({});
  // Map "seriesId:dateStr" -> dayId for override updates
  const dayIdLookup = useRef<Record<string, string>>({});

  const existingNames = useMemo(
    () => new Set(existingFunctions.map((f) => f.name.toLowerCase())),
    [existingFunctions],
  );

  const eventDaySet = useMemo(
    () => new Set(eventDays.map(toDateStr)),
    [eventDays],
  );

  // Initialize rows when dialog opens and data loads
  useEffect(() => {
    if (!defaultFunctions || !open) return;

    const dayCountValues = Object.values(cameraDayCounts);
    const cameraOpId = initConfig?.cameraOperatorFunctionId ?? null;
    const droneOpId = initConfig?.droneOperatorFunctionId ?? null;

    // Default day selection for new rows: event days if available, else all
    const defaultSelectedDays: Record<string, boolean> = {};
    for (const day of planningDays) {
      const dateStr = toDateStr(day);
      defaultSelectedDays[dateStr] =
        eventDays.length > 0 ? eventDaySet.has(dateStr) : true;
    }

    // --- Existing function rows ---
    const existingRows: FunctionRowState[] = existingFunctions
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((series) => {
        const selectedDays: Record<string, boolean> = {};
        for (const day of planningDays) {
          const dateStr = toDateStr(day);
          selectedDays[dateStr] = series.selected_days.includes(dateStr);
        }

        const dayOverrides: Record<string, number | undefined> = {};
        for (const d of series.days) {
          if (d.required_count_override !== null) {
            dayOverrides[d.function_date] = d.required_count_override;
          }
          // Build day ID lookup
          dayIdLookup.current[`${series.id}:${d.function_date}`] = d.id;
        }

        return {
          existingSeriesId: series.id,
          defaultFunctionId: null,
          projectFunctionGroupId: series.project_function_group_id ?? null,
          projectFunctionGroupName: series.project_function_group?.name ?? null,
          projectFunctionGroupLocationType:
            series.project_function_group?.location_type ?? null,
          name: series.name,
          systemTag: series.system_tag,
          locationType: series.location_type,
          enabled: true,
          defaultCount: series.required_count,
          dayOverrides,
          selectedDays,
          daySelectionMode: getDaySelectionMode(
            planningDays,
            eventDaySet,
            selectedDays,
          ),
        };
      });

    // Store originals for diffing
    const originals: Record<string, FunctionRowState> = {};
    for (const row of existingRows) {
      originals[row.existingSeriesId!] = {
        ...row,
        dayOverrides: { ...row.dayOverrides },
        selectedDays: { ...row.selectedDays },
      };
    }
    originalExistingRows.current = originals;

    // --- New template rows ---
    const visibleFunctions = defaultFunctions
      .filter((fn) => fn.show_on_initialize)
      .filter((fn) => !existingNames.has(fn.name.toLowerCase()))
      .sort((a, b) => a.name.localeCompare(b.name));

    const templateRows: FunctionRowState[] = visibleFunctions.map((fn) => {
      const isCameraOperator = cameraOpId !== null && fn.id === cameraOpId;
      const isDroneOperator = droneOpId !== null && fn.id === droneOpId;

      let defaultCount = 1;
      const dayOverrides: Record<string, number | undefined> = {};

      if (dayCountValues.length > 0) {
        if (isDroneOperator) {
          const maxCount = Math.max(...dayCountValues.map((c) => c.drone));
          if (maxCount > 0) {
            defaultCount = maxCount;
            for (const [date, c] of Object.entries(cameraDayCounts)) {
              if (c.drone !== defaultCount) {
                dayOverrides[date] = c.drone;
              }
            }
          }
        } else if (isCameraOperator) {
          const maxCount = Math.max(...dayCountValues.map((c) => c.camera));
          if (maxCount > 0) {
            defaultCount = maxCount;
            for (const [date, c] of Object.entries(cameraDayCounts)) {
              if (c.camera !== defaultCount) {
                dayOverrides[date] = c.camera;
              }
            }
          }
        }
      }

      return {
        existingSeriesId: null,
        defaultFunctionId: fn.id,
        projectFunctionGroupId:
          projectFunctionGroups.find(
            (group) => group.name === fn.default_function_group?.name,
          )?.id ?? null,
        projectFunctionGroupName: fn.default_function_group?.name ?? null,
        projectFunctionGroupLocationType:
          fn.default_function_group?.location_type ?? null,
        name: fn.name,
        systemTag: fn.system_tag,
        locationType: fn.location_type ?? null,
        enabled: mode === "initialize",
        defaultCount,
        dayOverrides,
        selectedDays: { ...defaultSelectedDays },
        daySelectionMode: eventDays.length > 0 ? "event" : "all",
      };
    });

    setRows([...existingRows, ...templateRows]);
  }, [
    defaultFunctions,
    cameraDayCounts,
    open,
    existingNames,
    existingFunctions,
    initConfig,
    projectFunctionGroups,
    eventDaySet,
    eventDays.length,
    planningDays,
    mode,
  ]);

  const handleToggleFunction = (index: number) => {
    setRows((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, enabled: !row.enabled } : row,
      ),
    );
  };

  const handleDefaultCountChange = (index: number, value: number) => {
    setRows((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const newDefault = Math.max(0, value);
        const newOverrides: Record<string, number | undefined> = {};
        for (const [date, override] of Object.entries(row.dayOverrides)) {
          if (override !== undefined && override !== newDefault) {
            newOverrides[date] = override;
          }
        }
        return { ...row, defaultCount: newDefault, dayOverrides: newOverrides };
      }),
    );
  };

  const handleToggleDay = (index: number, dateStr: string) => {
    setRows((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        return {
          ...row,
          selectedDays: {
            ...row.selectedDays,
            [dateStr]: !row.selectedDays[dateStr],
          },
          daySelectionMode: "custom",
        };
      }),
    );
  };

  const handleDayCountChange = (
    index: number,
    dateStr: string,
    value: string,
  ) => {
    setRows((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const newOverrides = { ...row.dayOverrides };

        if (value.trim() === "") {
          delete newOverrides[dateStr];
          return { ...row, dayOverrides: newOverrides };
        }

        const parsedValue = Number.parseInt(value, 10);
        const val = Number.isNaN(parsedValue) ? 0 : Math.max(0, parsedValue);

        if (val === row.defaultCount) {
          delete newOverrides[dateStr];
        } else {
          newOverrides[dateStr] = val;
        }
        return { ...row, dayOverrides: newOverrides };
      }),
    );
  };

  const handleDaySelectionModeChange = (
    index: number,
    selectionMode: "all" | "event",
  ) => {
    setRows((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const newSelected: Record<string, boolean> = {};
        for (const day of planningDays) {
          const dateStr = toDateStr(day);
          newSelected[dateStr] =
            selectionMode === "all" ? true : eventDaySet.has(dateStr);
        }
        return {
          ...row,
          selectedDays: newSelected,
          daySelectionMode: selectionMode,
        };
      }),
    );
  };

  const handleSubmit = () => {
    // Phase 1: Update existing functions that changed
    for (const row of rows) {
      if (!row.existingSeriesId) continue;
      const original = originalExistingRows.current[row.existingSeriesId];
      if (!original) continue;

      // Check series-level changes
      const updates: Record<string, unknown> = {};
      if (row.defaultCount !== original.defaultCount) {
        updates.required_count = row.defaultCount;
      }
      if (row.systemTag !== original.systemTag) {
        updates.system_tag = row.systemTag;
      }
      if (row.projectFunctionGroupId !== original.projectFunctionGroupId) {
        updates.project_function_group_id = row.projectFunctionGroupId;
      }
      if (row.locationType !== original.locationType) {
        updates.location_type = row.locationType;
      }

      const newSelectedDays = planningDays
        .map(toDateStr)
        .filter((d) => row.selectedDays[d]);
      const origSelectedDays = planningDays
        .map(toDateStr)
        .filter((d) => original.selectedDays[d]);
      if (
        JSON.stringify(newSelectedDays) !== JSON.stringify(origSelectedDays)
      ) {
        updates.selected_days = newSelectedDays;
      }

      if (Object.keys(updates).length > 0) {
        onUpdateSeries(
          row.existingSeriesId,
          updates as Parameters<typeof onUpdateSeries>[1],
        );
      }

      // Check day override changes (only for days that existed before)
      for (const [dateStr, origOverride] of Object.entries(
        original.dayOverrides,
      )) {
        const newOverride = row.dayOverrides[dateStr];
        if (newOverride !== origOverride) {
          const dayId =
            dayIdLookup.current[`${row.existingSeriesId}:${dateStr}`];
          if (dayId) {
            onUpdateDayOverride(
              dayId,
              newOverride !== undefined && newOverride !== row.defaultCount
                ? newOverride
                : null,
            );
          }
        }
      }
      // Check for new overrides on days that previously had no override
      for (const [dateStr, newOverride] of Object.entries(row.dayOverrides)) {
        if (
          newOverride !== undefined &&
          original.dayOverrides[dateStr] === undefined
        ) {
          const dayId =
            dayIdLookup.current[`${row.existingSeriesId}:${dateStr}`];
          if (dayId) {
            onUpdateDayOverride(
              dayId,
              newOverride !== row.defaultCount ? newOverride : null,
            );
          }
        }
      }
    }

    // Phase 2: Create new functions
    const functionsToCreate = rows
      .filter((row) => !row.existingSeriesId && row.enabled)
      .map((row) => {
        const days = planningDays
          .map(toDateStr)
          .filter((d) => row.selectedDays[d]);
        return {
          name: row.name,
          project_function_group_id: row.projectFunctionGroupId,
          project_function_group_name: row.projectFunctionGroupName,
          project_function_group_location_type:
            row.projectFunctionGroupLocationType,
          required_count: row.defaultCount,
          selected_days: days,
          system_tag: row.systemTag,
          day_overrides: Object.entries(row.dayOverrides)
            .filter(
              ([date, count]) =>
                count !== undefined &&
                count !== row.defaultCount &&
                row.selectedDays[date],
            )
            .map(([date, count]) => ({ date, count: count! })),
        };
      })
      .filter((fn) => fn.selected_days.length > 0);

    if (functionsToCreate.length > 0) {
      onInitialize(functionsToCreate);
    }

    setOpen(false);
  };

  const existingRowCount = rows.filter(
    (r) => r.existingSeriesId !== null,
  ).length;
  const newEnabledCount = rows.filter(
    (r) => !r.existingSeriesId && r.enabled,
  ).length;
  const projectGroupOrder = useMemo(
    () =>
      new Map(
        projectFunctionGroups.map(
          (group) => [group.id, group.display_order] as const,
        ),
      ),
    [projectFunctionGroups],
  );
  const templateGroupOptions = useMemo(() => {
    const options = new Map<
      string,
      { name: string; locationType: LocationType }
    >();
    for (const row of rows) {
      if (row.projectFunctionGroupId || !row.projectFunctionGroupName) continue;
      options.set(row.projectFunctionGroupName, {
        name: row.projectFunctionGroupName,
        locationType: row.projectFunctionGroupLocationType,
      });
    }
    return [...options.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);
  const groupedRows = useMemo(() => {
    const grouped = new Map<
      string,
      { key: string; label: string; order: number; rowIndexes: number[] }
    >();

    rows.forEach((row, index) => {
      const key = getRowGroupKey(row);
      const current = grouped.get(key);
      if (current) {
        current.rowIndexes.push(index);
        return;
      }

      grouped.set(key, {
        key,
        label: getRowGroupLabel(row),
        order:
          row.projectFunctionGroupId != null
            ? (projectGroupOrder.get(row.projectFunctionGroupId) ?? 0)
            : row.projectFunctionGroupName
              ? 10_000
              : 20_000,
        rowIndexes: [index],
      });
    });

    return [...grouped.values()].sort(
      (a, b) => a.order - b.order || a.label.localeCompare(b.label),
    );
  }, [projectGroupOrder, rows]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className={cn(triggerClassName)}>
          {mode === "initialize" ? (
            <>
              <Wand2 className="h-4 w-4 mr-2" />
              Initialize Functions
            </>
          ) : (
            <>
              <Pencil className="h-4 w-4 mr-2" />
              Bulk Update Functions
            </>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[90vw] max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            {mode === "initialize"
              ? "Initialize Project Functions"
              : "Bulk Update Functions"}
          </DialogTitle>
          <DialogDescription>
            {mode === "initialize"
              ? "Create functions from templates. Camera operator and drone counts are pre-filled from camera plans."
              : "Edit existing functions and optionally add new ones from templates."}
          </DialogDescription>
        </DialogHeader>

        {loadingDefaults ? (
          <div className="space-y-3 py-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : !defaultFunctions?.length && mode === "initialize" ? (
          <div className="py-8 text-center text-muted-foreground">
            No default function templates found. Create templates in Settings
            first.
          </div>
        ) : (
          <>
            <div className="flex-1 min-h-0 overflow-auto border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 z-20 bg-background min-w-[220px]">
                      Function
                    </TableHead>
                    <TableHead className="text-center min-w-[80px]">
                      Default #
                    </TableHead>
                    {planningDays.map((day) => {
                      const dateStr = toDateStr(day);
                      const isEvent = eventDaySet.has(dateStr);
                      return (
                        <TableHead
                          key={dateStr}
                          className={`text-center min-w-[70px] whitespace-nowrap ${isEvent ? "font-bold" : ""}`}
                        >
                          <div className="text-xs font-normal">
                            {format(day, "EEE")}
                          </div>
                          <div>{format(day, "dd.MM")}</div>
                        </TableHead>
                      );
                    })}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupedRows.map((group) => (
                    <Fragment key={group.key}>
                      <TableRow>
                        <TableCell
                          colSpan={2 + planningDays.length}
                          className="sticky left-0 z-10 bg-muted/70 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                        >
                          {group.label}
                        </TableCell>
                      </TableRow>
                      {group.rowIndexes.map((index) => {
                        const row = rows[index];
                        const isExisting = row.existingSeriesId !== null;
                        const isDisabled = !isExisting && !row.enabled;
                        const rowKey = isExisting
                          ? row.existingSeriesId!
                          : row.defaultFunctionId!;

                        return (
                          <TableRow
                            key={rowKey}
                            className={isDisabled ? "opacity-50" : ""}
                          >
                            <TableCell className="sticky left-0 z-10 bg-background">
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  {!isExisting && (
                                    <Checkbox
                                      checked={row.enabled}
                                      onCheckedChange={() =>
                                        handleToggleFunction(index)
                                      }
                                    />
                                  )}
                                  <span className="text-sm font-medium">
                                    {row.name}
                                  </span>
                                  {isExisting ? (
                                    <Badge
                                      variant="secondary"
                                      className="text-xs"
                                    >
                                      existing
                                    </Badge>
                                  ) : (
                                    <Badge
                                      variant="outline"
                                      className="text-xs"
                                    >
                                      template
                                    </Badge>
                                  )}
                                  {initConfig?.cameraOperatorFunctionId ===
                                    row.defaultFunctionId && (
                                    <Badge
                                      variant="outline"
                                      className="text-xs"
                                    >
                                      cam
                                    </Badge>
                                  )}
                                  {initConfig?.droneOperatorFunctionId ===
                                    row.defaultFunctionId && (
                                    <Badge
                                      variant="outline"
                                      className="text-xs"
                                    >
                                      drone
                                    </Badge>
                                  )}
                                </div>
                                {(isExisting || row.enabled) && (
                                  <div
                                    className={`flex flex-wrap gap-2 ${isExisting ? "" : "ml-6"}`}
                                  >
                                    <Select
                                      value={getGroupSelectValue(row)}
                                      onValueChange={(value) =>
                                        setRows((prev) =>
                                          prev.map((item, itemIndex) => {
                                            if (itemIndex !== index) {
                                              return item;
                                            }
                                            if (value === "none") {
                                              return {
                                                ...item,
                                                projectFunctionGroupId: null,
                                                projectFunctionGroupName: null,
                                                projectFunctionGroupLocationType:
                                                  null,
                                              };
                                            }
                                            if (value.startsWith("template:")) {
                                              const name = value.slice(
                                                "template:".length,
                                              );
                                              const selectedGroup =
                                                templateGroupOptions.find(
                                                  (option) =>
                                                    option.name === name,
                                                );
                                              return {
                                                ...item,
                                                projectFunctionGroupId: null,
                                                projectFunctionGroupName: name,
                                                projectFunctionGroupLocationType:
                                                  selectedGroup?.locationType ??
                                                  null,
                                              };
                                            }
                                            const selectedGroup =
                                              projectFunctionGroups.find(
                                                (group) => group.id === value,
                                              );
                                            return {
                                              ...item,
                                              projectFunctionGroupId: value,
                                              projectFunctionGroupName:
                                                selectedGroup?.name ?? null,
                                              projectFunctionGroupLocationType:
                                                selectedGroup?.location_type ??
                                                null,
                                            };
                                          }),
                                        )
                                      }
                                    >
                                      <SelectTrigger className="h-7 w-[130px] text-xs">
                                        <SelectValue placeholder="No group" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="none">
                                          No group
                                        </SelectItem>
                                        {projectFunctionGroups.map((group) => (
                                          <SelectItem
                                            key={group.id}
                                            value={group.id}
                                          >
                                            {group.name}
                                          </SelectItem>
                                        ))}
                                        {templateGroupOptions.map((group) => (
                                          <SelectItem
                                            key={group.name}
                                            value={`template:${group.name}`}
                                          >
                                            {group.name}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <Select
                                      value={row.daySelectionMode}
                                      onValueChange={(value) => {
                                        if (
                                          value === "all" ||
                                          value === "event"
                                        ) {
                                          handleDaySelectionModeChange(
                                            index,
                                            value,
                                          );
                                        }
                                      }}
                                    >
                                      <SelectTrigger className="h-7 w-[120px] text-xs">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="all">All</SelectItem>
                                        {eventDays.length > 0 && (
                                          <SelectItem value="event">
                                            Event dates
                                          </SelectItem>
                                        )}
                                        {row.daySelectionMode === "custom" && (
                                          <SelectItem value="custom">
                                            Custom
                                          </SelectItem>
                                        )}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Input
                                type="number"
                                min={0}
                                value={row.defaultCount}
                                onChange={(e) =>
                                  handleDefaultCountChange(
                                    index,
                                    parseInt(e.target.value) || 0,
                                  )
                                }
                                disabled={isDisabled}
                                className="w-16 mx-auto text-center h-8"
                              />
                            </TableCell>
                            {planningDays.map((day) => {
                              const dateStr = toDateStr(day);
                              const isChecked =
                                row.selectedDays[dateStr] ?? false;
                              const isOverridden =
                                row.dayOverrides[dateStr] !== undefined;
                              return (
                                <TableCell
                                  key={dateStr}
                                  className="text-center"
                                >
                                  <div className="flex flex-col items-center gap-1">
                                    <Checkbox
                                      checked={isChecked}
                                      disabled={isDisabled}
                                      onCheckedChange={() =>
                                        handleToggleDay(index, dateStr)
                                      }
                                    />
                                    {isChecked && !isDisabled && (
                                      <Input
                                        type="number"
                                        min={0}
                                        value={
                                          row.dayOverrides[dateStr] !==
                                          undefined
                                            ? String(row.dayOverrides[dateStr])
                                            : ""
                                        }
                                        placeholder={String(row.defaultCount)}
                                        onChange={(e) =>
                                          handleDayCountChange(
                                            index,
                                            dateStr,
                                            e.target.value,
                                          )
                                        }
                                        className={`w-14 text-center h-6 text-xs ${
                                          isOverridden
                                            ? "border-blue-400 bg-blue-50 text-blue-950 dark:bg-blue-950/50 dark:text-blue-50"
                                            : ""
                                        }`}
                                      />
                                    )}
                                  </div>
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  (mode === "initialize" && newEnabledCount === 0) || isLoading
                }
              >
                {isLoading
                  ? "Saving..."
                  : mode === "initialize"
                    ? `Initialize ${newEnabledCount} Function${newEnabledCount !== 1 ? "s" : ""}`
                    : `Save Changes${newEnabledCount > 0 ? ` (+${newEnabledCount} new)` : ""}`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
